import * as XLSX from "xlsx-js-style";

const LEGUP_EXPORT_TYPE = "LEG_UP_PUBLIC_SIMULATION";

function normaliseText(value) {
  return String(value ?? "")
    .trim()
    .toUpperCase();
}

function readKeyValueSheet(sheet) {
  if (!sheet) {
    return {};
  }

  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: "",
  });

  const values = {};

  rows.forEach((row) => {
    const key = String(row?.[0] ?? "").trim();

    if (!key || key === "Key") {
      return;
    }

    values[key] = row?.[1] ?? "";
  });

  return values;
}

function setPathValue(target, path, value) {
  const tokens = String(path || "")
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean);

  if (!tokens.length) {
    return;
  }

  let current = target;

  tokens.forEach((token, index) => {
    const isLast = index === tokens.length - 1;

    const nextToken = tokens[index + 1];

    const nextIsArrayIndex = /^\d+$/.test(String(nextToken ?? ""));

    const tokenIsArrayIndex = /^\d+$/.test(token);

    const key = tokenIsArrayIndex ? Number(token) : token;

    if (isLast) {
      current[key] = value;
      return;
    }

    if (
      current[key] === undefined ||
      current[key] === null ||
      typeof current[key] !== "object"
    ) {
      current[key] = nextIsArrayIndex ? [] : {};
    }

    current = current[key];
  });
}

function readInputsSheet(sheet) {
  if (!sheet) {
    throw new Error("The workbook does not contain an Inputs sheet.");
  }

  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: "",
  });

  let headerIndex = -1;
  let keyColumn = -1;
  let valueColumn = -1;

  rows.some((row, index) => {
    const cells = row.map((value) =>
      String(value ?? "")
        .trim()
        .toLowerCase(),
    );

    /*
     * Schema 1.1+
     * Key | Field | Value | Unit | Explanation
     */
    if (cells[0] === "key" && cells[2] === "value") {
      headerIndex = index;
      keyColumn = 0;
      valueColumn = 2;

      return true;
    }

    /*
     * Backwards compatibility with the first export:
     * Path | Value | Type
     */
    if (cells[0] === "path" && cells[1] === "value") {
      headerIndex = index;
      keyColumn = 0;
      valueColumn = 1;

      return true;
    }

    return false;
  });

  if (headerIndex < 0 || keyColumn < 0 || valueColumn < 0) {
    throw new Error(
      "The Inputs sheet does not have the expected LEG-UP format.",
    );
  }

  const inputs = {};
  let importedCount = 0;

  rows.slice(headerIndex + 1).forEach((row) => {
    const path = String(row?.[keyColumn] ?? "").trim();

    if (!path) {
      return;
    }

    const value = row?.[valueColumn];

    setPathValue(inputs, path, value);

    importedCount += 1;
  });

  if (!importedCount) {
    throw new Error("The workbook does not contain any reusable input values.");
  }

  return inputs;
}

export async function readSimulationWorkbook(
  file,
  { expectedSimulationType, expectedRegion } = {},
) {
  if (!file) {
    throw new Error("Choose an Excel file first.");
  }

  const fileName = String(file.name || "").toLowerCase();

  if (!fileName.endsWith(".xlsx")) {
    throw new Error("Please choose a LEG-UP .xlsx simulation file.");
  }

  let workbook;

  try {
    const buffer = await file.arrayBuffer();

    workbook = XLSX.read(buffer, {
      type: "array",
      cellDates: true,
    });
  } catch (error) {
    console.error("[LEG-UP] Could not read uploaded workbook:", error);

    throw new Error("The selected Excel file could not be read.");
  }

  const technicalSheet = workbook.Sheets["LEGUP_DATA"];

  if (!technicalSheet) {
    throw new Error("This is not a recognised LEG-UP simulation workbook.");
  }

  const metadata = readKeyValueSheet(technicalSheet);

  if (metadata.export_type && metadata.export_type !== LEGUP_EXPORT_TYPE) {
    throw new Error("This workbook is not a LEG-UP public simulation export.");
  }

  const actualType = normaliseText(metadata.simulation_type);

  const requiredType = normaliseText(expectedSimulationType);

  if (requiredType && actualType && requiredType !== actualType) {
    throw new Error("This workbook belongs to a different LEG-UP simulator.");
  }

  const actualRegion = normaliseText(metadata.region);

  const requiredRegion = normaliseText(expectedRegion);

  if (requiredRegion && actualRegion && requiredRegion !== actualRegion) {
    throw new Error(
      "This workbook belongs to a different LEG-UP pilot region.",
    );
  }

  const canonicalInputSheet = String(
    metadata.canonical_input_sheet || "Inputs",
  ).trim();

  const inputSheet =
    workbook.Sheets[canonicalInputSheet] || workbook.Sheets.Inputs;

  const inputs = readInputsSheet(inputSheet);

  return {
    inputs,
    metadata,
    workbook,
  };
}

export function asText(value, fallback = "") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
}

export function asFiniteNumber(value, fallback = 0) {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

export function clampNumber(value, min, max, fallback) {
  const parsed = asFiniteNumber(value, fallback);

  return Math.min(max, Math.max(min, parsed));
}

export function asArray(value) {
  return Array.isArray(value) ? value : [];
}
